import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {KairosMark} from '@/components/kairos-mark'
import {AmberDot} from '@/components/amber-dot'
import {NoAccessState} from '@/components/no-access-state'
import {EntrevistaCompletionSummary} from '@/components/entrevista-completion-summary'
import {EstadoDeArranque} from '@/components/resumen-arranque'
import {ResumenEstrategico} from '@/components/resumen-estrategico'
import {ResumenOperativo} from '@/components/resumen-operativo'
import {RosterDriftNotice} from '@/components/roster-drift-notice'
import {HomeLinkCard, MemberHome} from '@/pages/empresa/mi_espacio/member-home'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useDashboard} from '@/hooks/sgsst/resumen/use-dashboard'
import {useMyOnboarding} from '@/hooks/empresa/identidad/use-onboarding'
import {useMyLicencia} from '@/hooks/inci/consola/use-licencia'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useRuta, type Ruta} from '@/hooks/sgsst/resumen/use-ruta'

/** Sin selector: la lente sale de quién es la persona. La licencia es de la persona (`/me/licencia`);
 * `tiene_sst_verificado` es de la empresa y daría la lente mixta a quien solo contrató un profesional. */
type Lente = 'estrategica' | 'operativa' | 'mixta'

function PasoGuino({ruta, businessId}: {ruta: Ruta; businessId: string}) {
  const {t} = useTranslation()
  return (
    <Link
      to={`/businesses/${businessId}/ruta`}
      className="surface surface-raised flex items-center justify-between gap-4 px-5 py-4"
      data-testid="paso-guino">
      <span className="flex items-center gap-2.5">
        {ruta.espera_al_usuario && <AmberDot label={t('common.senal.esperaTuAccion')} testId="paso-guino-dot" />}
        <span className="font-mono text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
          {t('home.paso.label', {n: ruta.paso_actual})}
        </span>
      </span>
      <span className="text-[13px] font-semibold text-primary">{t('home.paso.cta')} →</span>
    </Link>
  )
}

/** Solo la lente mixta se tabula: un dueño con licencia es las dos personas a la vez; abre en «Trabajo». */
type HomeTab = 'trabajo' | 'proteccion'

/** KAI-219: el pendiente propio de quien opera no secuestra la consola (solo `member-home.tsx` redirige),
 * pero su dato cuenta en los agregados de la Res. 0312/2019. */
function PerfilPendientePropio({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: onboarding} = useMyOnboarding(businessId)
  if (onboarding?.pendiente?.cta !== 'completar_perfil') return null
  return (
    <HomeLinkCard
      to={`/businesses/${businessId}/mi/ficha`}
      eyebrow={t('member.home.perfilSociodemograficoEyebrow')}
      title={onboarding.pendiente.mensaje}
      cta={t('member.home.perfilSociodemograficoLink')}
      testId="home-perfil-pendiente-propio"
    />
  )
}

export function BusinessHomePage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {data: business, isLoading} = useBusiness(businessId)
  const capabilities = useCapabilities(businessId)
  const canOperate = capabilities.sgsst_operar
  // Quien puede operar el SG-SST (identity-access-spec §1a.3) ve el Resumen operativo; un miembro raso, MemberHome.
  const {data: sgsst} = useSgsst(canOperate ? businessId : undefined)
  const {data: ruta} = useRuta(canOperate ? businessId : undefined)
  const isOwner = business?.role === 'owner'
  // Solo se pregunta cuando puede cambiar la lente: a un responsable la licencia no le mueve nada.
  const {data: licencia} = useMyLicencia(canOperate && isOwner)
  const {data: dashboard} = useDashboard(canOperate && sgsst?.assessment ? businessId : undefined)

  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!business) return <NoAccessState />

  if (!canOperate) {
    return <MemberHome businessId={businessId!} businessName={business.name} />
  }

  if (!sgsst || !ruta) return <Skeleton className="h-40 w-full" />

  const assessment = sgsst.assessment
  const lente: Lente = !isOwner ? 'operativa' : licencia?.estado === 'verificada' ? 'mixta' : 'estrategica'
  const homeTab: HomeTab = searchParams.get('tab') === 'proteccion' ? 'proteccion' : 'trabajo'
  function setHomeTab(next: HomeTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }
  // La reconciliación de nómina solo aparece cuando el capítulo NO cambiaría; una
  // deriva que cambia el régimen es trabajo del RegimeDriftBanner, nunca los dos.
  const rosterMismatch = !!assessment && !assessment.drift && sgsst.active_employee_count !== assessment.worker_count

  return (
    <div className="space-y-6" data-lente={lente}>
      <div>
        <div className="flex items-center gap-2 text-foreground">
          <KairosMark />
          <h1 className="font-heading text-2xl">{business.name}</h1>
        </div>
      </div>

      <EntrevistaCompletionSummary businessId={businessId!} canRemind={isOwner} />

      <PasoGuino ruta={ruta} businessId={businessId!} />

      {capabilities.tiene_membresia_real && <PerfilPendientePropio businessId={businessId!} />}

      {rosterMismatch && (
        <RosterDriftNotice
          businessId={businessId!}
          declared={assessment!.worker_count}
          active={sgsst.active_employee_count}
        />
      )}

      {/* Sin evaluación el Resumen es el estado de arranque; un tablero de ceros no ayuda. */}
      {assessment ? (
        dashboard &&
        (lente === 'mixta' ? (
          <Tabs value={homeTab} onValueChange={(v) => setHomeTab(v as HomeTab)}>
            <TabsList>
              <TabsTrigger value="trabajo" data-testid="home-tab-trabajo">
                {t('home.tabs.trabajo')}
              </TabsTrigger>
              <TabsTrigger value="proteccion" data-testid="home-tab-proteccion">
                {t('home.tabs.proteccion')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="trabajo" className="pt-4">
              <ResumenOperativo dashboard={dashboard} state={sgsst} businessId={businessId!} ruta={ruta} />
            </TabsContent>
            <TabsContent value="proteccion" className="pt-4">
              <ResumenEstrategico
                dashboard={dashboard}
                state={sgsst}
                businessId={businessId!}
                capabilities={capabilities}
                ruta={ruta}
              />
            </TabsContent>
          </Tabs>
        ) : lente === 'estrategica' ? (
          <ResumenEstrategico
            dashboard={dashboard}
            state={sgsst}
            businessId={businessId!}
            capabilities={capabilities}
            ruta={ruta}
          />
        ) : (
          <ResumenOperativo dashboard={dashboard} state={sgsst} businessId={businessId!} ruta={ruta} />
        ))
      ) : (
        <EstadoDeArranque ruta={ruta} businessId={businessId!} bienvenida />
      )}
    </div>
  )
}
